import React from "react";
import { useAuthCtx } from "./Auth";
import { UseToggle, useToggle } from "@/shared/base/hooks/useToggle";
import { FraudWarningModal } from "@/shared/modules/PurchaseSeats/Checkout/Payment/PaymentDetails/FraudWarningModal";
import { useMutation } from "@tanstack/react-query";
import {
  addAccountRestrictionApi,
  addBrokerEmailApi,
  checkBrokerEmailApi,
} from "../api/broker";
import { MutationType } from "@/types";
import { useAppSystem } from "../hooks/useAppSystem";
import { useAppStore } from "../store";
import { selectUser } from "../store/data/user";
import { vendorUUIDs } from "../config/constants";
import { useTicketsdotcom } from "../hooks/useTicketsdotcom";
import { useInternalAuth } from "../hooks/useInternalAuth";

interface CtxType {
  paymentFailCount: number;
  zipFailCount: number;
  fraudWarningModal: UseToggle;
  updatePaymentFailCount: (a?: { onSuccess?: (a: number) => void }) => void;
  updateZipFailCount: (a?: { onSuccess?: (a: number) => void }) => void;
  addBrokerEmail: MutationType<Parameters<typeof addBrokerEmailApi>[0]>;
  checkBrokerEmail: MutationType<Parameters<typeof checkBrokerEmailApi>[0]>;
  updateBrokerEmail: () => Promise<void>;
  handleAddAccountRestriction: (a: string, b?: any, c?: any) => Promise<void>;
  handleCheckBrokerEmail: () => Promise<boolean>;
  handleBrokerLogout: () => void;
}

const FraudPreventionCtx = React.createContext<CtxType | null>(null);
export const useFraudPrevention = () =>
  React.useContext(FraudPreventionCtx) as CtxType;

export function FraudPreventionProvider(props: { children: React.ReactNode }) {
  const { teamConfig, router } = useAppSystem();
  const { teamSchema, platform, oktaClientId, oktaIssuer } = teamConfig;
  const { logoutTicketMaster } = useAuthCtx();
  const user = useAppStore(selectUser);
  const urlHasBrokerParam = router?.query?.isBroker;
  const urlErrorMessage = router?.query?.errorMessage as string;
  const { handleSignOut } = useTicketsdotcom();
  const { handleInternalLogOut, shouldUseInternalLogin } = useInternalAuth();

  const [paymentFailCount, setPaymentFailCount] = React.useState(0);
  const [zipFailCount, setZipFailCount] = React.useState(0);
  const [isClosingFraudModal, setIsClosingFraudModal] = React.useState(false);
  const fraudWarningModal = useToggle();
  const addBrokerEmail = useMutation(addBrokerEmailApi);
  const checkBrokerEmail = useMutation(checkBrokerEmailApi);
  const addAccountRestriction = useMutation(addAccountRestrictionApi);

  const updatePaymentFailCount: CtxType["updatePaymentFailCount"] = (args) => {
    setPaymentFailCount((prev) => {
      const newCount = prev + 1;
      args?.onSuccess?.(newCount);
      return newCount;
    });
  };

  const updateZipFailCount: CtxType["updateZipFailCount"] = (args) => {
    setZipFailCount((prev) => {
      const newCount = prev + 1;
      args?.onSuccess?.(newCount);
      return newCount;
    });
  };

  const updateBrokerEmail = async () => {
    try {
      let brokerApiPayload = { email: user?.email, teamSchema };
      await addBrokerEmail.mutateAsync(brokerApiPayload);
    } catch (error) {
      console.log(error);
    }
  };

  const handleBrokerLogout = (message?: string) => {
    if (platform === vendorUUIDs.ticketmaster) {
      const logoutUrlParams = Object.create(null);
      logoutUrlParams.isBroker = true;
      if (message) logoutUrlParams.errorMessage = message;
      logoutTicketMaster(logoutUrlParams);
    }

    if (!!oktaIssuer && !!oktaClientId) {
      if (shouldUseInternalLogin) return handleInternalLogOut();

      handleSignOut("/");
    }
  };

  const handleAddAccountRestriction = async (
    reason: string,
    accountId?: string,
    email?: string
  ) => {
    try {
      let payload = {
        teamSchema,
        email: email || user?.email,
        accountId: accountId || user?.accountId,
        reason,
      };

      await addAccountRestriction.mutateAsync(payload, {
        onSuccess: ({ data }: any) => {
          handleBrokerLogout(data?.errorMessage);
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleCheckBrokerEmail = async () => {
    let isBroker = false;
    try {
      let payload = { email: user?.email, teamSchema };
      await checkBrokerEmail.mutateAsync(payload, {
        onSuccess: ({ data }) => {
          if (data?.isBroker) {
            isBroker = true;
            handleBrokerLogout(data?.errorMessage);
          }
        },
      });
    } catch (error) {
      console.log(error);
    }
    return isBroker;
  };

  const handleFraudModalClose = () => {
    const routerQueryUpdated = { ...router?.query };
    delete routerQueryUpdated["isBroker"];
    delete routerQueryUpdated["errorMessage"];
    router?.push({ query: routerQueryUpdated });
    fraudWarningModal.onClose();
  };

  React.useEffect(() => {
    if (user) handleCheckBrokerEmail();
  }, []);

  React.useEffect(() => {
    if (urlHasBrokerParam) {
      fraudWarningModal.onOpen();
    }
  }, [urlHasBrokerParam]);

  return (
    <>
      <FraudWarningModal
        modalProps={fraudWarningModal}
        onClose={handleFraudModalClose}
        isLoadingClose={isClosingFraudModal}
        errorMessage={
          urlErrorMessage ? decodeURIComponent(urlErrorMessage) : undefined
        }
      />
      <FraudPreventionCtx.Provider
        value={{
          paymentFailCount,
          zipFailCount,
          updatePaymentFailCount,
          updateZipFailCount,
          fraudWarningModal,
          addBrokerEmail,
          checkBrokerEmail,
          updateBrokerEmail,
          handleAddAccountRestriction,
          handleCheckBrokerEmail,
          handleBrokerLogout,
        }}
      >
        {props.children}
      </FraudPreventionCtx.Provider>
    </>
  );
}
